import { Children, isValidElement, useEffect, useState, } from 'react';
import { useCanAccessResources, useRecordContext, useResourceContext, } from 'react-admin';
/**
 * A hook that filters children based on the user's permissions.
 * Note that it requires the children to be valid React elements and having a `source` prop.
 * It will always render children that are not React elements or that do not have a `source` prop.
 * @param options {Object} The hook props
 * @param options.children {ReactNode} The children to filter
 * @param options.action {string} The action to check for. Defaults to 'read'
 * @param options.record {Object} The record to check for. Defaults to the record from RecordContext
 * @param options.resource {string} The resource to check for. Defaults to the resource from ResourceContext
 * @param options.getResourceKey {Function} A function to get the resource key from a child. Defaults to `${resource}.${child.props.source}`
 * @param options.filterChildren {Function} A function to filter children for which to check permissions. Defaults to `child => child.props.source != null`
 * @returns {Object} An array of the children users have access to, the pending state, and the error
 *
 * @example
 * // in src/MyCustomShowLayout.tsx
 * import * as React from 'react';
 * import { useAuthorizedChildren } from '@react-admin/ra-rbac';
 * import { TextField } from 'react-admin';
 *
 * const MyCustomShowLayout = ({ children }: { children: React.ReactNode }) => {
 *   const { authorizedChildren, isPending, error } = useAuthorizedChildren({
 *     children,
 *   });
 *
 *   return (
 *     <Stack>
 *       {authorizedChildren}
 *     </Stack>
 *   );
 * };
 *
 * // in src/posts/PostShow.tsx
 * import * as React from 'react';
 * import { TextField, Show } from 'react-admin';
 * import { MyCustomShowLayout } from '../MyCustomShowLayout';
 *
 * export const PostShow = (props) => (
 *   <Show {...props}>
 *     <MyCustomShowLayout>
 *       <TextField source="title" />
 *       <TextField source="body" />
 *     </MyCustomShowLayout>
 *   </Show>
 */
export var useAuthorizedChildren = function (options) {
    var children = options.children, _a = options.action, action = _a === void 0 ? 'read' : _a, _b = options.enabled, enabled = _b === void 0 ? false : _b, _c = options.filterChildren, filterChildren = _c === void 0 ? DefaultFilterChildren : _c, _d = options.getResourceKey, getResourceKey = _d === void 0 ? DefaultGetResourceKey : _d;
    var resource = useResourceContext(options);
    if (!resource) {
        throw new Error('useAuthorizedChildren used outside of a ResourceContext. You must pass a resource prop.');
    }
    var record = useRecordContext(options);
    var _e = useState(enabled ? [] : Children.toArray(children)), authorizedChildren = _e[0], setAuthorizedChildren = _e[1];
    var childrenResource = Children.toArray(children)
        .filter(function (child) { return isValidElement(child) && filterChildren(child, resource); })
        .map(function (child) { return getResourceKey(child, resource); });
    var _f = useCanAccessResources({
        resources: childrenResource,
        action: action,
        record: record,
        enabled: enabled,
    }), canAccess = _f.canAccess, isPending = _f.isPending, error = _f.error;
    useEffect(function () {
        if (!enabled || isPending || canAccess == null) {
            return;
        }
        var authorizedChildren = Children.toArray(children).filter(function (child) {
            // This will exclude children that are not React elements or that don't match the filter (by default, children without a `source` prop)
            if (!isValidElement(child) || !filterChildren(child, resource)) {
                return true;
            }
            return canAccess[getResourceKey(child, resource)];
        });
        setAuthorizedChildren(authorizedChildren);
    }, [
        canAccess,
        children,
        enabled,
        filterChildren,
        getResourceKey,
        isPending,
        resource,
    ]);
    return {
        authorizedChildren: enabled ? authorizedChildren : children,
        isPending: enabled ? isPending : false,
        error: enabled ? error : null,
    };
};
var DefaultFilterChildren = function (child) {
    return child.props.source != null;
};
var DefaultGetResourceKey = function (child, resource) {
    return "".concat(resource, ".").concat(child.props.source);
};
